import React, { useEffect, useRef, useState } from 'react';
import h337 from 'heatmap.js';
import { Loader2, Info, X } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Field-engagement heatmap.
 *
 * Renders a stripped-down list of form elements and overlays a heatmap.js
 * gradient on top, where:
 *   - WARM (red)   = high drop-off / friction → respondents bail here
 *   - COOL (blue)  = low drop-off → field flows well
 *
 * Data comes from `/api/analytics/forms/{formId}/elements` which we already
 * fetch in Analytics.js — we accept the parsed payload as a prop here so we
 * don't double-fetch.
 *
 * We deliberately do NOT render an iframe of the real form. Iframes block
 * cross-origin DOM measurement, and we don't need pixel-perfect fidelity —
 * we need a clear, side-by-side answer to "which field is killing my
 * conversion?". Cards laid out vertically match the funnel mental model.
 */
const FormHeatmap = ({ form, elementAnalytics, onClose }) => {
  const containerRef = useRef(null);
  const heatmapRef = useRef(null);
  const cardRefs = useRef({});
  const [hoveredId, setHoveredId] = useState(null);

  // Only elements we actually rendered (excluding pure layout elements that
  // have no engagement signal of their own).
  const trackedTypes = new Set([
    'short_text', 'long_text', 'email', 'number', 'phone', 'url',
    'single_choice', 'multiple_choice', 'dropdown', 'rating', 'linear_scale',
    'yes_no', 'matrix', 'ranked_list', 'fill_in_blanks', 'full_name',
    'address', 'date', 'time', 'date_time', 'file_upload', 'signature',
    'voice_recording', 'payment', 'clickwrap',
  ]);
  const elements = (elementAnalytics?.elements || []).filter((e) => trackedTypes.has(e.type));

  // Recompute the heatmap whenever the layout changes (mount, resize, hover).
  useEffect(() => {
    if (!containerRef.current || elements.length === 0) return;

    // Destroy any previous instance so re-renders don't stack canvases.
    const container = containerRef.current;
    container.querySelectorAll('canvas.heatmap-canvas').forEach((c) => c.remove());

    const instance = h337.create({
      container,
      radius: 110,
      maxOpacity: 0.55,
      minOpacity: 0,
      blur: 0.85,
      // Custom gradient: blue (cool, smooth flow) → green → yellow → red (hot, friction)
      gradient: {
        0.0: 'rgba(59, 130, 246, 0)',
        0.25: 'rgba(59, 130, 246, 0.6)',
        0.5: 'rgba(34, 197, 94, 0.7)',
        0.7: 'rgba(250, 204, 21, 0.8)',
        0.9: 'rgba(254, 106, 107, 0.9)',
        1.0: 'rgba(220, 38, 38, 1)',
      },
    });
    heatmapRef.current = instance;
    // Tag the canvas so we can clean it up on re-render.
    const canvases = container.querySelectorAll('canvas');
    canvases.forEach((c) => c.classList.add('heatmap-canvas'));

    // Build data points: one per element, positioned at the center of its
    // rendered card. Value = drop_off (0..100). We add small jitter so the
    // gradient blends instead of producing perfectly stacked dots.
    const containerRect = container.getBoundingClientRect();
    const points = elements
      .map((el) => {
        const card = cardRefs.current[el.id];
        if (!card) return null;
        const rect = card.getBoundingClientRect();
        return {
          x: Math.round(rect.left - containerRect.left + rect.width / 2),
          y: Math.round(rect.top - containerRect.top + rect.height / 2),
          value: Math.max(0, Math.min(100, el.drop_off || 0)),
        };
      })
      .filter(Boolean);

    instance.setData({ max: 100, min: 0, data: points });

    const onResize = () => {
      // heatmap.js doesn't react to container resize on its own.
      // Recompute positions and replot.
      const newRect = container.getBoundingClientRect();
      const fresh = elements.map((el) => {
        const card = cardRefs.current[el.id];
        if (!card) return null;
        const r = card.getBoundingClientRect();
        return {
          x: Math.round(r.left - newRect.left + r.width / 2),
          y: Math.round(r.top - newRect.top + r.height / 2),
          value: Math.max(0, Math.min(100, el.drop_off || 0)),
        };
      }).filter(Boolean);
      instance.setData({ max: 100, min: 0, data: fresh });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [elements]);

  if (!elementAnalytics) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="heatmap-loading">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (elements.length === 0) {
    return (
      <div className="rounded-xl border border-zinc-200 bg-zinc-50/40 p-8 text-center" data-testid="heatmap-empty">
        <p className="text-sm text-zinc-500">
          There is not enough data to display. Check back later.
        </p>
      </div>
    );
  }

  // Require a meaningful amount of activity before plotting a heatmap.
  // Below this threshold the gradients are dominated by noise (one early
  // drop-off can swing a field from "cool" to "hot") and lead the form
  // owner to bad decisions.
  const totalCompleted = elementAnalytics.total_completed || 0;
  const totalAbandoned = elementAnalytics.total_abandoned || 0;
  const totalActivity = totalCompleted + totalAbandoned;
  const MIN_ACTIVITY = 10;
  if (totalActivity < MIN_ACTIVITY || totalAbandoned === 0) {
    return (
      <div className="space-y-4" data-testid="heatmap-insufficient-data">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-zinc-400 mt-0.5 shrink-0" />
            <p className="text-xs text-zinc-500">
              <strong className="text-zinc-700">Field-engagement heatmap</strong>
            </p>
          </div>
          {onClose && (
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="heatmap-close-btn" aria-label="Close heatmap">
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
        <div className="rounded-xl border border-zinc-200 bg-zinc-50/40 p-10 text-center">
          <p className="text-sm text-zinc-600 font-medium">There is not enough data to display.</p>
          <p className="text-xs text-zinc-500 mt-1">Check back later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="form-heatmap">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 text-zinc-400 mt-0.5 shrink-0" />
          <p className="text-xs text-zinc-500 max-w-2xl">
            <strong className="text-zinc-700">Field-engagement heatmap</strong> — warm areas indicate where respondents
            drop off ({elementAnalytics.total_completed} completed + {elementAnalytics.total_abandoned} abandoned).
            Hover over a field card to see its exact metrics.
          </p>
        </div>
        {onClose && (
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="heatmap-close-btn" aria-label="Close heatmap">
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 text-[11px] text-zinc-500" data-testid="heatmap-legend">
        <span>Smooth flow</span>
        <div
          className="flex-1 h-2 rounded-full"
          style={{
            background:
              'linear-gradient(to right, rgba(59,130,246,0.7), rgba(34,197,94,0.7), rgba(250,204,21,0.85), rgba(254,106,107,0.9), rgba(220,38,38,1))',
          }}
        />
        <span>High friction</span>
      </div>

      {/* Heatmap canvas container */}
      <div
        ref={containerRef}
        className="relative rounded-xl border border-zinc-200 bg-white p-6 space-y-3 overflow-hidden"
        data-testid="heatmap-container"
      >
        {/* Form title pseudo-header for visual context */}
        <div className="pb-2 border-b border-zinc-100">
          <h3 className="font-bold text-zinc-900">{form?.title || 'Form Preview'}</h3>
          {form?.description && (
            <p className="text-xs text-zinc-500 mt-0.5 line-clamp-1">{form.description}</p>
          )}
        </div>

        {elements.map((el, idx) => {
          const isHot = (el.drop_off || 0) >= 50;
          const isMedium = (el.drop_off || 0) >= 20 && (el.drop_off || 0) < 50;
          return (
            <div
              key={el.id}
              ref={(node) => { cardRefs.current[el.id] = node; }}
              className={`relative z-10 rounded-lg border bg-white/90 backdrop-blur-sm p-3 transition-all cursor-default ${
                hoveredId === el.id ? 'ring-2 ring-zinc-900 shadow-md' : 'border-zinc-200'
              }`}
              onMouseEnter={() => setHoveredId(el.id)}
              onMouseLeave={() => setHoveredId(null)}
              data-testid={`heatmap-card-${el.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] text-zinc-400 font-mono">#{idx + 1}</span>
                    <span className="text-[10px] uppercase tracking-wide text-zinc-500">{el.type}</span>
                    {el.required && (
                      <span className="text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">required</span>
                    )}
                  </div>
                  <p className="text-sm font-medium text-zinc-900 truncate" title={el.question || ''}>
                    {el.question || <span className="italic text-zinc-400">(no label)</span>}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <div
                    className={`text-sm font-bold ${
                      isHot ? 'text-red-600' : isMedium ? 'text-amber-600' : 'text-zinc-700'
                    }`}
                  >
                    {(el.drop_off || 0).toFixed(0)}%
                  </div>
                  <div className="text-[10px] text-zinc-400 uppercase tracking-wide">drop-off</div>
                </div>
              </div>
              {hoveredId === el.id && (
                <div className="mt-2 pt-2 border-t border-zinc-100 flex items-center gap-4 text-[11px] text-zinc-500" data-testid={`heatmap-card-detail-${el.id}`}>
                  <span>Reached: <strong className="text-zinc-700">{el.reached || 0}</strong></span>
                  <span>Answered: <strong className="text-zinc-700">{el.completed_with_answer || 0}</strong></span>
                  <span>Fill rate: <strong className="text-zinc-700">{(el.fill_rate || 0).toFixed(1)}%</strong></span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FormHeatmap;
